/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import {
    Loader,
    Message,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from 'semantic-ui-react'
import { validate } from 'barcoder'
import Barcode from 'react-barcode'
import { Link, useSearchParams } from 'react-router-dom-v5-compat'
import { useTranslation } from 'react-i18next'
import useUser from '../../Shared/UserProvider/useUser'
import { UltyMoney } from '@ulty/ulty-ui'
import React from 'react'

const List = ({unavailableItems, pagination}) => {
    const [t] = useTranslation()
    const {user} = useUser()
    const [searchParams, setSearchParams] = useSearchParams()

    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const sortBy = searchParams.get('sortBy')
    const direction = searchParams.get('direction')
    if (!sortBy || !direction) {
        searchParams.set('sortBy', 'totalPrice')
        searchParams.set('direction', 'descending')
        setSearchParams(searchParams, { replace: true })
        return null
    }

    if (!from || !to || !unavailableItems) {
        return <Loader active/>
    }

    if (unavailableItems.length === 0) {
        return (
          <div
            css={css`
                display: flex;
                justify-content: center;
            `}
          >
              <Message compact>
                  {t('unavailable_items.no_unavailable_product')}
              </Message>
          </div>
        )
    }

    const handleSortByColumn = (columnName) => {
        searchParams.set('sortBy', columnName)
        searchParams.set('direction', direction === 'ascending' ? 'descending' : 'ascending')
        setSearchParams(searchParams, { replace: true })
    }

    return (
      <>
          {unavailableItems.some(unavailableItem => unavailableItem.totalPrice === undefined) && <i css={css`
              font-size: small;
          `}>
              {t('unavailable_items.missing_data')}
          </i>}
          <Table singleLine sortable>
              <TableHeader>
                  <TableRow>
                      <TableHeaderCell width={2}>{t('unavailable_items.product.barcode')}</TableHeaderCell>
                      <TableHeaderCell width={10}>{t('unavailable_items.product.name')}</TableHeaderCell>
                      <TableHeaderCell
                        width={1}
                        onClick={() => handleSortByColumn('totalUnitCount')}
                        sorted={searchParams.get('sortBy') === 'totalUnitCount' ? searchParams.get('direction') : null}
                      >{t('unavailable_items.product.missed_quantity')}
                      </TableHeaderCell>
                      <TableHeaderCell
                        width={1}
                        onClick={() => handleSortByColumn('totalPriceUnavailable')}
                        sorted={searchParams.get('sortBy') === 'totalPriceUnavailable' ?
                          searchParams.get('direction') :
                          searchParams.get('sortBy') === null ?
                            'descending' :
                            null
                        }
                      >
                          {t('unavailable_items.product.missed_revenue_unavailable')}
                      </TableHeaderCell>
                      <TableHeaderCell
                        width={1}
                        onClick={() => handleSortByColumn('totalPriceSubstituted')}
                        sorted={searchParams.get('sortBy') === 'totalPriceSubstituted' ?
                          searchParams.get('direction') :
                          searchParams.get('sortBy') === null ?
                            'descending' :
                            null
                        }
                      >
                          {t('unavailable_items.product.missed_revenue_substituted')}
                      </TableHeaderCell>
                      <TableHeaderCell
                        width={1}
                        onClick={() => handleSortByColumn('totalPrice')}
                        sorted={searchParams.get('sortBy') === 'totalPrice' ?
                          searchParams.get('direction') :
                          searchParams.get('sortBy') === null ?
                            'descending' :
                            null
                        }
                      >
                          {t('unavailable_items.product.missed_revenue')}
                      </TableHeaderCell>
                  </TableRow>
              </TableHeader>
              <TableBody>
                  {unavailableItems.map(unavailableItem => (
                    <TableRow
                      key={unavailableItem.id}
                      css={css`
                          height: 85px;
                      `}>
                        <TableCell textAlign="center">
                            {validate(unavailableItem.barcode) ?
                              <Barcode
                                value={unavailableItem.barcode}
                                format={unavailableItem.barcode.length === 8 ? 'EAN8' :
                                  unavailableItem.barcode.length === 13 ? 'EAN13' :
                                    undefined}
                                height={20}
                                width={1}
                              /> :
                              unavailableItem.barcode}
                        </TableCell>
                        <TableCell>
                            {unavailableItem.isProvidedByCurrentLoginContext ? (
                              <Link
                                to={`/products/${unavailableItem.id}`}
                                css={css`
                                    white-space: break-spaces;
                                `}
                              >
                                  {unavailableItem.name}
                              </Link>
                            ) : (
                              <div
                                css={css`
                                    white-space: break-spaces;
                                `}
                              >
                                  {unavailableItem.name}
                              </div>
                            )}
                        </TableCell>
                        <TableCell>{unavailableItem.quantity} {(unavailableItem?.sellableUnit?.type !== 'COUNT' && unavailableItem?.sellableUnit?.unit) && t(`measurement_unit.symbols.${unavailableItem.sellableUnit.unit}`)}</TableCell>
                        <TableCell>
                            {unavailableItem.totalPriceUnavailable ?
                              <div css={css`
                                  display: flex;
                              `}>
                                  <UltyMoney
                                    readonly
                                    amount={unavailableItem.totalPriceUnavailable}
                                    currencyCode={user.getCompanyCurrency()}
                                    locale={user.getCompanyLocale()}
                                  />
                              </div> : '-'}
                        </TableCell>
                        <TableCell>
                            {unavailableItem.totalPriceSubstituted ?
                              <div css={css`
                                  display: flex;
                              `}>
                                  <UltyMoney
                                    readonly
                                    amount={unavailableItem.totalPriceSubstituted}
                                    currencyCode={user.getCompanyCurrency()}
                                    locale={user.getCompanyLocale()}
                                  />
                              </div> : '-'}
                        </TableCell>
                        <TableCell>
                            {unavailableItem.totalPrice ?
                              <div css={css`
                                  display: flex;
                              `}>
                                  <UltyMoney
                                    readonly
                                    amount={unavailableItem.totalPrice * -1}
                                    currencyCode={user.getCompanyCurrency()}
                                    locale={user.getCompanyLocale()}
                                  />
                              </div> : '-'}
                        </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
          </Table>

          {pagination && (<div style={{display: 'flex', justifyContent: 'center', marginTop: 20}}>
              <Pagination
                activePage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={(_, data) => {
                    searchParams.set('page', data.activePage)
                    setSearchParams(searchParams, { replace: true })
                }}
                siblingRange={1}
                boundaryRange={1}
              />
          </div>)}
      </>
    )
}

export { List }
